import { MONTH_KEYS } from "../constants"

export function formatTabulatorAggsResponse(aggs: any): TabulatorRow[] {
  const sortByNameAsc = (a: TabulatorRow, b: TabulatorRow) =>
    a.name.localeCompare(b.name, 'es', { numeric: true, sensitivity: 'base' })

  if (!aggs?.divisiones?.buckets) return []

  function getMonthKey(mes: number) { return MONTH_KEYS[mes - 1] }
  const sum = (arr: number[]) => arr.reduce((a, b) => a + b, 0)

  const divisiones = aggs.divisiones.buckets
  const result: TabulatorRow[] = []

  for (const division of divisiones) {
    const divisionName =
      division.nombre_division?.hits?.hits?.[0]?._source?.division || division.key

    const zonasRows: TabulatorRow[] = []
    let divisionPromedios: number[] = []

    for (const zona of division.zonas.buckets) {
      const zonaName =
        zona.nombre_zona?.hits?.hits?.[0]?._source?.zona || zona.key

      const unidadesRows: TabulatorRow[] = []
      let zonaPromedios: number[] = []

      for (const unidad of zona.unidades.buckets) {
        const unidadSource = unidad.nombre_unidad?.hits?.hits?.[0]?._source || {}
        const unidadName = unidadSource.unidad || unidad.key

        const unidadRow: TabulatorRow = { name: unidadName }
        if (unidadSource.semana) unidadRow.semana = unidadSource.semana
        if (unidadSource.fecha) unidadRow.fecha = unidadSource.fecha

        const unidadPromedios: number[] = []

        for (let i = 1; i <= 12; i++) {
          const mesBucket = unidad.meses?.buckets?.find((m: any) => Number(m.key) === i)

          if (mesBucket) {
            const total = mesBucket.total?.value ?? 0
            const finalizados = mesBucket.finalizados?.doc_count ?? 0
            const porcentaje = total > 0 ? (finalizados / total) * 100 : 0
            const val = Number(porcentaje.toFixed(2))

            unidadRow[getMonthKey(i)] = val
            unidadPromedios.push(val)
          } else {
            unidadRow[getMonthKey(i)] = '-'
          }
        }

        unidadRow.total = unidadPromedios.length > 0 ? Number(sum(unidadPromedios).toFixed(2)) : '-'
        unidadRow.promedio = unidadPromedios.length > 0 ? Number((sum(unidadPromedios) / unidadPromedios.length).toFixed(2)) : '-'

        unidadesRows.push(unidadRow)
        zonaPromedios.push(...unidadPromedios)
      }

      const zonaRow: TabulatorRow = { name: zonaName }
      const zonaMeses: number[] = []

      for (let i = 1; i <= 12; i++) {
        const mesPorcentajes = zona.unidades.buckets
          .map((unidad: any) => {
            const mesBucket = unidad.meses?.buckets?.find((m: any) => Number(m.key) === i)
            const total = mesBucket?.total?.value ?? 0
            const finalizados = mesBucket?.finalizados?.doc_count ?? 0
            return total > 0 ? (finalizados / total) * 100 : null
          })
          .filter((v: number | null) => v !== null) as number[]

        if (mesPorcentajes.length > 0) {
          const avg = sum(mesPorcentajes) / mesPorcentajes.length
          const val = Number(avg.toFixed(2))
          zonaRow[getMonthKey(i)] = val
          zonaMeses.push(val)
        } else {
          zonaRow[getMonthKey(i)] = '-'
        }
      }

      zonaRow.total = zonaMeses.length > 0 ? Number(sum(zonaMeses).toFixed(2)) : '-'
      zonaRow.promedio = zonaPromedios.length > 0 ? Number((sum(zonaPromedios) / zonaPromedios.length).toFixed(2)) : '-'
      zonaRow.subTitle = 'Unidades'
      zonaRow.subRows = unidadesRows

      zonasRows.push(zonaRow)
      divisionPromedios.push(...zonaPromedios)
    }

    const divisionRow: TabulatorRow = { name: divisionName }
    const divisionMeses: number[] = []

    for (let i = 1; i <= 12; i++) {
      const zonaPorcentajes = division.zonas.buckets
        .map((zona: any) => {
          const mesPorcentajes = zona.unidades.buckets
            .map((unidad: any) => {
              const mesBucket = unidad.meses?.buckets?.find((m: any) => Number(m.key) === i)
              const total = mesBucket?.total?.value ?? 0
              const finalizados = mesBucket?.finalizados?.doc_count ?? 0
              return total > 0 ? (finalizados / total) * 100 : null
            })
            .filter((v: number | null) => v !== null) as number[]

          return mesPorcentajes.length > 0 ? (sum(mesPorcentajes) / mesPorcentajes.length) : null
        })
        .filter((v: number | null) => v !== null) as number[]

      if (zonaPorcentajes.length > 0) {
        const avg = sum(zonaPorcentajes) / zonaPorcentajes.length
        const val = Number(avg.toFixed(2))
        divisionRow[getMonthKey(i)] = val
        divisionMeses.push(val)
      } else {
        divisionRow[getMonthKey(i)] = '-'
      }
    }

    divisionRow.total = divisionMeses.length > 0 ? Number(sum(divisionMeses).toFixed(2)) : '-'
    divisionRow.promedio = divisionPromedios.length > 0 ? Number((sum(divisionPromedios) / divisionPromedios.length).toFixed(2)) : '-'
    divisionRow.subTitle = 'Zonas'

    zonasRows.sort(sortByNameAsc)
    divisionRow.subRows = zonasRows
    result.push(divisionRow)
  }

  result.sort(sortByNameAsc)
  return result
}

export function formatTabulatorOpenSourceResponse(hits: any[]): TabulatorRow[] {
  if (!Array.isArray(hits)) return []
  function getMonthKey(mes: number) { return MONTH_KEYS[mes - 1] }
  const sum = (arr: number[]) => arr.reduce((a, b) => a + b, 0)

  const divisiones = new Map<
    string,
    {
      nombre: string
      zonas: Map<
        string,
        {
          nombre: string
          unidades: Map<
            string,
            { nombre: string; meses: Map<number, { total: number; finalizados: number }> }
          >
        }
      >
    }
  >()

  for (const hit of hits) {
    const s = hit._source
    const divisionId = String(s.division_id)
    const zonaId = String(s.zona_id)
    const unidadId = String(s.unidad_id)
    const mes = Number(s.mes)
    const semana = s.semana
    const fecha = s.fecha

    if (!divisiones.has(divisionId)) {
      divisiones.set(divisionId, { nombre: s.division, zonas: new Map() })
    }

    const zonas = divisiones.get(divisionId)!.zonas
    if (!zonas.has(zonaId)) {
      zonas.set(zonaId, { nombre: s.zona, unidades: new Map() })
    }

    const unidades = zonas.get(zonaId)!.unidades
    if (!unidades.has(unidadId)) {
      unidades.set(unidadId, { nombre: s.unidad, meses: new Map() })
    }

    const meses = unidades.get(unidadId)!.meses
    if (!meses.has(mes)) {
      meses.set(mes, { total: 0, finalizados: 0 })
    }

    const mesObj = meses.get(mes)!
    mesObj.total += 1
    if (s.finalizado) mesObj.finalizados += 1
  }

  const result: TabulatorRow[] = []

  for (const [, division] of divisiones) {
    const zonasRows: TabulatorRow[] = []
    let divisionPromedios: number[] = []

    for (const [, zona] of division.zonas) {
      const unidadesRows: TabulatorRow[] = []
      let zonaPromedios: number[] = []

      for (const [, unidad] of zona.unidades) {
        const unidadRow: TabulatorRow = { name: unidad.nombre }
        const unidadPromedios: number[] = []

        for (let i = 1; i <= 12; i++) {
          const mesObj = unidad.meses.get(i)

          if (mesObj) {
            const porcentaje = mesObj.total > 0 ? (mesObj.finalizados / mesObj.total) * 100 : 0
            const val = Number(porcentaje.toFixed(2))
            unidadRow[getMonthKey(i)] = val
            unidadPromedios.push(val)
          } else {
            unidadRow[getMonthKey(i)] = '-'
          }
        }

        unidadRow.total = unidadPromedios.length > 0 ? Number(sum(unidadPromedios).toFixed(2)) : '-'
        unidadRow.promedio = unidadPromedios.length > 0 ? Number((sum(unidadPromedios) / unidadPromedios.length).toFixed(2)) : '-'

        unidadesRows.push(unidadRow)
        zonaPromedios.push(...unidadPromedios)
      }

      const zonaRow: TabulatorRow = { name: zona.nombre }
      const zonaMeses: number[] = []

      for (let i = 1; i <= 12; i++) {
        const mesPorcentajes = Array.from(zona.unidades.values())
          .map(unidad => {
            const mesObj = unidad.meses.get(i)
            return mesObj ? (mesObj.total > 0 ? (mesObj.finalizados / mesObj.total) * 100 : 0) : null
          })
          .filter((v): v is number => v !== null)

        if (mesPorcentajes.length > 0) {
          const avg = sum(mesPorcentajes) / mesPorcentajes.length
          const val = Number(avg.toFixed(2))
          zonaRow[getMonthKey(i)] = val
          zonaMeses.push(val)
        } else {
          zonaRow[getMonthKey(i)] = '-'
        }
      }

      zonaRow.total = zonaMeses.length > 0 ? Number(sum(zonaMeses).toFixed(2)) : '-'
      zonaRow.promedio = zonaPromedios.length > 0 ? Number((sum(zonaPromedios) / zonaPromedios.length).toFixed(2)) : '-'
      zonaRow.subTitle = 'Unidades'
      zonaRow.subRows = unidadesRows

      zonasRows.push(zonaRow)
      divisionPromedios.push(...zonaPromedios)
    }

    const divisionRow: TabulatorRow = { name: division.nombre }
    const divisionMeses: number[] = []

    for (let i = 1; i <= 12; i++) {
      const zonaPorcentajes = Array.from(division.zonas.values())
        .map(zona => {
          const mesPorcentajes = Array.from(zona.unidades.values())
            .map(unidad => {
              const mesObj = unidad.meses.get(i)
              return mesObj ? (mesObj.total > 0 ? (mesObj.finalizados / mesObj.total) * 100 : 0) : null
            })
            .filter((v): v is number => v !== null)

          return mesPorcentajes.length > 0 ? (sum(mesPorcentajes) / mesPorcentajes.length) : null
        })
        .filter((v): v is number => v !== null)

      if (zonaPorcentajes.length > 0) {
        const avg = sum(zonaPorcentajes) / zonaPorcentajes.length
        const val = Number(avg.toFixed(2))
        divisionRow[getMonthKey(i)] = val
        divisionMeses.push(val)
      } else {
        divisionRow[getMonthKey(i)] = '-'
      }
    }

    divisionRow.total = divisionMeses.length > 0 ? Number(sum(divisionMeses).toFixed(2)) : '-'
    divisionRow.promedio = divisionPromedios.length > 0 ? Number((sum(divisionPromedios) / divisionPromedios.length).toFixed(2)) : '-'
    divisionRow.subTitle = 'Zonas'
    divisionRow.subRows = zonasRows

    result.push(divisionRow)
  }

  return result
}

interface Source {
  division_id: string
  division: string
  zona_id: string
  zona: string
  unidad_id: string
  unidad: string
  mes: string | number
  semana: string | number
}

interface BackendHit {
  _source: Source
}

export interface TabulatorRow {
  name: string
  [key: string]: any
  subRows?: TabulatorRow[]
  subTitle?: string
}

const PROGRAMADOS_POR_UNIDAD = 30

function getMonthKey(mes: number) {
  return MONTH_KEYS[mes - 1]
}

export function formatTabulatorResponse(hits: BackendHit[]): TabulatorRow[] {
  const divisiones = new Map<string, { zonas: Map<string, { unidades: Map<string, Source[]> }> }>()

  for (const hit of hits) {
    const s = hit._source
    const mes = typeof s.mes === 'string' ? Number(s.mes) : s.mes
    const semana = typeof s.semana === 'string' ? Number(s.semana) : s.semana

    if (!divisiones.has(s.division)) {
      divisiones.set(s.division, { zonas: new Map() })
    }

    const zonas = divisiones.get(s.division)!.zonas
    if (!zonas.has(s.zona)) {
      zonas.set(s.zona, { unidades: new Map() })
    }

    const unidades = zonas.get(s.zona)!.unidades
    if (!unidades.has(s.unidad)) {
      unidades.set(s.unidad, [])
    }

    unidades.get(s.unidad)!.push({ ...s, mes, semana })
  }

  const result: TabulatorRow[] = []

  for (const [division, { zonas }] of divisiones) {
    const mesesDivision: { [mes: number]: { finalizados: number; programados: number } } = {}
    const zonasRows: TabulatorRow[] = []

    for (const [zona, { unidades }] of zonas) {
      const mesesZona: { [mes: number]: { finalizados: number; programados: number } } = {}
      const unidadesRows: TabulatorRow[] = []

      for (const [unidad, registros] of unidades) {
        const mesesUnidad: { [mes: number]: { finalizados: number; programados: number } } = {}

        for (const reg of registros) {
          const mesNum = typeof reg.mes === 'string' ? Number(reg.mes) : reg.mes

          if (!mesesUnidad[mesNum]) {
            mesesUnidad[mesNum] = { finalizados: 0, programados: 0 }
          }

          mesesUnidad[mesNum].finalizados += 1
          mesesUnidad[mesNum].programados = PROGRAMADOS_POR_UNIDAD
        }

        const unidadRow: TabulatorRow = { name: unidad }

        for (let i = 1; i <= 12; i++) {
          const key = getMonthKey(i)
          if (mesesUnidad[i]) {
            const { finalizados } = mesesUnidad[i]
            unidadRow[key] = finalizados
          } else {
            unidadRow[key] = '-'
          }
        }

        const totalFinalizados = Object.values(mesesUnidad).reduce((acc, v) => acc + v.finalizados, 0)
        unidadRow.total = totalFinalizados

        const mesesConValor = Object.values(mesesUnidad).filter(v => v.finalizados > 0)
        unidadRow.promedio = mesesConValor.length > 0
          ? (mesesConValor.reduce((acc, v) => acc + v.finalizados, 0) / mesesConValor.length).toFixed(2)
          : '-'

        unidadesRows.push(unidadRow)

        for (const [mes, { finalizados, programados }] of Object.entries(mesesUnidad)) {
          const mesNum = Number(mes)
          if (!mesesZona[mesNum]) mesesZona[mesNum] = { finalizados: 0, programados: 0 }
          mesesZona[mesNum].finalizados += finalizados
          mesesZona[mesNum].programados += programados
        }
      }

      const zonaRow: TabulatorRow = { name: zona }

      for (let i = 1; i <= 12; i++) {
        const key = getMonthKey(i)
        if (mesesZona[i]) {
          const { finalizados } = mesesZona[i]
          zonaRow[key] = finalizados
        } else {
          zonaRow[key] = '-'
        }
      }

      const totalFinalizadosZona = Object.values(mesesZona).reduce((acc, v) => acc + v.finalizados, 0)
      zonaRow.total = totalFinalizadosZona

      const mesesConValorZona = Object.values(mesesZona).filter(v => v.finalizados > 0)
      zonaRow.promedio = mesesConValorZona.length > 0
        ? (mesesConValorZona.reduce((acc, v) => acc + v.finalizados, 0) / mesesConValorZona.length).toFixed(2)
        : '-'

      zonaRow.subTitle = 'Unidades'
      zonaRow.subRows = unidadesRows
      zonasRows.push(zonaRow)

      for (const [mes, { finalizados, programados }] of Object.entries(mesesZona)) {
        const mesNum = Number(mes)
        if (!mesesDivision[mesNum]) mesesDivision[mesNum] = { finalizados: 0, programados: 0 }
        mesesDivision[mesNum].finalizados += finalizados
        mesesDivision[mesNum].programados += programados
      }
    }

    const divisionRow: TabulatorRow = { name: division }

    for (let i = 1; i <= 12; i++) {
      const key = getMonthKey(i)
      if (mesesDivision[i]) {
        const { finalizados } = mesesDivision[i]
        divisionRow[key] = finalizados
      } else {
        divisionRow[key] = '-'
      }
    }

    const totalFinalizadosDivision = Object.values(mesesDivision).reduce((acc, v) => acc + v.finalizados, 0)
    divisionRow.total = totalFinalizadosDivision

    const mesesConValorDivision = Object.values(mesesDivision).filter(v => v.finalizados > 0)
    divisionRow.promedio = mesesConValorDivision.length > 0
      ? (mesesConValorDivision.reduce((acc, v) => acc + v.finalizados, 0) / mesesConValorDivision.length).toFixed(2)
      : '-'

    divisionRow.subTitle = 'Zonas'
    divisionRow.subRows = zonasRows
    result.push(divisionRow)
  }

  return result
}
